import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import DataTableCst, { DataTableColumn } from "@components/DataTableCst";
import { XapiStatement } from "../types";

type SortDir = "asc" | "desc";

interface XapiStatementsSectionProps {
  statements: XapiStatement[];
}

const formatTimestamp = (value?: string | null): string =>
  value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";

const XapiStatementsSection: React.FC<XapiStatementsSectionProps> = ({
  statements,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("enrollment-progress");
  const [sortField, setSortField] = useState<string | null>("timestamp");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const columns: DataTableColumn<XapiStatement>[] = [
    {
      key: "verb",
      label: String(t("xapi.columns.verb")),
      sortable: true,
      render: (row) => row.verb || "-",
    },
    {
      key: "score",
      label: String(t("xapi.columns.score")),
      sortable: true,
      render: (row) =>
        row.score === null || row.score === undefined ? "-" : row.score,
    },
    {
      key: "timestamp",
      label: String(t("xapi.columns.timestamp")),
      sortable: true,
      render: (row) => formatTimestamp(row.timestamp),
    },
  ];

  const rows = useMemo(() => {
    if (!sortField) return statements;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...statements].sort((a, b) => {
      const left = (a as any)[sortField] ?? "";
      const right = (b as any)[sortField] ?? "";
      if (left === right) return 0;
      return left > right ? dir : -dir;
    });
  }, [statements, sortField, sortDir]);

  const changeSort = (field: string) => {
    if (field === sortField) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDir("asc");
  };

  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      sx={{
        backgroundColor: "transparent",
        color: theme.palette.text.primary,
        "&::before": { display: "none" },
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <AccordionSummary
        expandIcon={
          <ChevronDown size={18} color={theme.palette.text.primary} />
        }
        sx={{ padding: "8px 12px" }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
          {t("xapi.statements")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "0 0 12px" }}>
        <DataTableCst<XapiStatement>
          columns={columns}
          rows={rows}
          getRowId={(_, index) => index}
          emptyLabel={String(t("xapi.statements-empty"))}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={changeSort}
          plain
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default XapiStatementsSection;
